<template>
	<div
		class="flow-input-wrapper"
		:class="{ prefix: prefix }"
		:boxType="boxType"
		:type="type"
		:state="state"
		:prefix="prefix"
		:inline="inline"
	>
		<div v-if="prefix" :multiline="multiline" :boxType="boxType" class="flow-input-prefix fc-light">
			{{ prefix }}
		</div>
		<input
			v-if="!multiline"
			ref="input-text"
			v-model="innerValue"
			:data-qa-field="dataQaField"
			:type="innerType"
			v-bind="{ ...$props, ...$attrs, ...nativeProps }"
			class="flow-input-text"
			@blur="handleBlur"
			@click="handleClick"
			@keyup="handleKeyup"
			@keypress="handleKeypress"
			@paste="handlePaste"
			@focus="handleFocus"
			@change="handleChange"
			@input="handleInput"
		/>
		<textarea
			v-if="multiline"
			ref="input-text"
			v-model="innerValue"
			:data-qa-field="dataQaField"
			v-bind="{ ...$props, ...$attrs }"
			class="flow-input-text"
			@blur="handleBlur"
			@click="handleClick"
			@keyup="handleKeyup"
			@keypress="handleKeypress"
			@paste="handlePaste"
			@focus="handleFocus"
			@change="handleChange"
			@input="handleInput"
		/>
		<div class="flow-input-state-icon" :stateText="stateText ? true : false">
			<Icon
				v-if="state === 'success'"
				:state="'success'"
				:size="'small'"
				:effects="false"
				name="i-tick"
				type="filled"
				:tooltip="successTooltip"
			/>
			<Icon
				v-if="state === 'error'"
				:state="'error'"
				:size="'small'"
				:effects="false"
				name="i-info-fill"
				type="filled"
				:tooltip="errorTooltip"
				@click="innerValue = ''"
			/>
			<Icon
				v-if="state === 'validating'"
				:state="'primary'"
				:size="'small'"
				:effects="false"
				name="i-loader"
				:animate="true"
			/>
			<Icon
				v-if="disabled"
				:state="'dark'"
				:size="'small'"
				:effects="false"
				:disabled="true"
				name="i-stop"
				type="outlined"
			/>
			<Icon
				v-if="(type === 'password' || type === 'text') && showPasswordEye"
				:state="showPassword ? 'primary' : 'default'"
				:size="'small'"
				:effects="false"
				name="i-view-fill"
				type="filled"
				@click="showPassword = !showPassword"
			/>
			<span v-if="stateText" class="i-state-text">{{ stateText }}</span>
		</div>
		<div v-if="suffixTag" class="flow-input-suffix-tag">
			<Tag :size="'small'" class="upper-case">
				{{ suffixTag }}
			</Tag>
		</div>
	</div>
</template>
<script lang="ts">
import { PropType, defineComponent } from "vue";
import Tag from "./../Tag/Tag.vue";
import Icon from "./../Icon/Icon.vue";

const TYPE = ["text", "password", "email", "url", "color", "number"] as const;
const BOX_TYPE = ["rounded", "curved"] as const;
const STATE = ["default", "error", "success", "validating"] as const;

/**
 * Text Filed is input element to that allows user to input the textual content.
 */
export default defineComponent({
	name: "InputText",
	components: {
		Icon,
		Tag
	},
	inheritAttrs: false,
	props: {
		/**
		 * 'text' | 'password' | 'email' | 'url' | 'color' | 'number'
		 */
		type: {
			type: String as PropType<typeof TYPE[number]>,
			default: "text"
			// validator(value: typeof TYPE[number]) {
			// 	return TYPE.includes(value);
			// }
		},

		/**
		 * 'curved' | 'rounded'
		 */
		boxType: {
			type: String as PropType<typeof BOX_TYPE[number]>,
			default: "curved"
			// validator(value: typeof BOX_TYPE[number]) {
			// 	return BOX_TYPE.includes(value);
			// }
		},
		/**
		 * Value of input field
		 */
		value: { type: [String, Number], default: null },
		/**
		 * true | false
		 */
		readonly: { type: Boolean, default: false },
		/**
		 * true | false
		 */
		disabled: { type: Boolean, default: false },
		/**
		 * Size of input
		 */
		size: { type: Number, default: null },
		/**
		 * Max number of characters
		 */
		maxlength: { type: Number, default: null },
		/**
		 * placeholder for input field
		 */
		placeholder: { type: String, default: "" },
		/**
		 * true | false
		 */
		autofocus: { type: Boolean, default: false },
		/**
		 * true | false
		 */
		multiline: { type: Boolean, default: false },
		/**
		 * true | false
		 */
		resize: { type: Boolean, default: false },
		/**
		 * 'error' | 'success' | 'default' | 'validating'
		 */
		state: {
			type: String as PropType<typeof STATE[number]>,
			default: "default"
			// validator(value: typeof STATE[number]) {
			// 	return STATE.includes(value);
			// }
		},
		/**
		 * state text : used to describle state <br/> E.g. Error, Invalid , Success ,...
		 */
		stateText: {
			type: String,
			default: null
		},
		/**
		 * prefix of input
		 */
		prefix: {
			type: String,
			default: null
		},
		/**
		 * suffix of input shown in tag
		 */
		suffixTag: {
			type: String,
			default: null
		},
		/**
		 * 'on' | 'off'
		 */ autocomplete: {
			type: String,
			default: "off"
		},
		removeFocusOnEnter: {
			type: Boolean,
			default: true
		},
		showPasswordEye: {
			type: Boolean,
			default: false
		},
		inline: {
			type: Boolean,
			default: false
		},
		dataQaField: {
			type: String,
			default: null
		},
		/**
		find and replace to specified character as user types in or paste something
		 */
		findAndReplace: {
			type: Object as PropType<{ find: string | null; replace: string | null }>,
			default: () => {
				return { find: null, replace: null };
			}
		},
		/**
		 * tooltip on green tick
		 */
		successTooltip: {
			type: String,
			default: "Valid"
		},
		/**
		 * tooltip on red i
		 */
		errorTooltip: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			typingEventTimeout: null as number | null,
			showPassword: false
		};
	},
	computed: {
		innerValue: {
			// getter
			get() {
				return this.value as string;
			},
			// setter
			set(newValue: string) {
				if (
					newValue &&
					this.findAndReplace &&
					this.findAndReplace.find &&
					this.findAndReplace.replace
				) {
					const regex = new RegExp(`${this.findAndReplace.find}`, "g");
					newValue = newValue.replace(regex, this.findAndReplace.replace);
				}
				/**
				 * Whenever value is updated.<br/>
				 * use 'value.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:value
				 */
				this.$emit("update:value", newValue);
			}
		},
		innerType() {
			return this.type === "password" && this.showPassword ? "text" : this.type;
		},
		nativeProps() {
			return this.innerType === "number" ? { min: 0 } : {};
		}
	},
	methods: {
		handleBlur(e: FocusEvent) {
			this.$emit("blur", e);
			this.handleDoneTyping();
		},
		handleClick(e: MouseEvent) {
			/**
			 * Whenever InputText is clicked.
			 *
			 * @event click
			 */
			this.$emit("click", e);
		},
		handleKeyup(e: KeyboardEvent) {
			/**
			 * keyup event
			 *
			 * @event keyup
			 */
			this.$emit("keyup", this.innerValue, e);
			// /**
			//  * blur event emitted when focus is removed from InputText
			//  *
			//  * @event blur
			//  */
			// this.$emit("blur", e);
			if (e.keyCode === 13) {
				if (this.removeFocusOnEnter) {
					//@ts-expect-error
					this.$refs["input-text"]?.blur();
				}
			} else {
				if (this.typingEventTimeout) {
					clearTimeout(this.typingEventTimeout);
				}
				this.typingEventTimeout = window.setTimeout(() => {
					this.handleDoneTyping();
				}, 3000);
			}
		},
		handleKeypress(e: KeyboardEvent) {
			/**
			 * keypress event
			 *
			 * @event keypress
			 */
			this.$emit("keypress", e);
		},
		handlePaste(e: ClipboardEvent) {
			/**
			 * paste event emitted when something is pasted in InputText
			 *
			 * @event paste
			 */
			this.$emit("paste", e);
			this.handleDoneTyping();
		},
		handleFocus(e: FocusEvent) {
			/**
			 * When InputText is focused by keyboard or mouse click.
			 *
			 * @event focus
			 */
			this.$emit("focus", e);
		},
		handleDoneTyping() {
			//	console.log("donetyping", this.innerValue);
			if (this.typingEventTimeout) {
				clearTimeout(this.typingEventTimeout);
			}
			/**
			 * donetyping event emiitted when <br/>
			 * 1. If there is not any activity from the user for 3 seconds from last keyup
			 * 2. If blur event is emitted
			 * 3. If paste event is emitted
			 *
			 * @event donetyping
			 */
			this.$emit("donetyping", this.innerValue);
		},
		handleChange(e: Event) {
			/**
			 * When InputText value changed.
			 *
			 * @event change
			 */
			this.$emit("change", e);
		},
		handleInput(e: Event) {
			if (this.findAndReplace && this.findAndReplace.find && this.findAndReplace.replace) {
				const regex = new RegExp(`${this.findAndReplace.find}`, "g");

				const target = e.target as HTMLInputElement | null;
				if (target) {
					target.value = target.value.replace(regex, this.findAndReplace.replace);
				}
			}
			/**
			 * When InputText value changes input event thrown.
			 *
			 * @event input
			 */
			this.$emit("input", e);
		}
	}
});
</script>
<style lang="scss">
@mixin input-text($background, $focus, $light-color, $text-color, $error, $success) {
	div.flow-input-wrapper {
		background-color: $background;

		> div.flow-input-prefix {
			background-color: $background;
			&:after {
				border-right: 2px solid $light-color;
			}
		}
		> div.flow-input-state-icon[stateText] {
			border-left: 2px solid $light-color;
		}
		> input.flow-input-text,
		> textarea.flow-input-text {
			background-color: $background !important;
			color: $text-color !important;

			appearance: unset !important;
			background-image: none !important;
			&:hover,
			&:active,
			&:focus {
				-webkit-box-shadow: 0 0 0 30px $background inset !important;
			}
			// &:not([state="error"]) {
			// 	&:focus {
			// 		box-shadow: inset 0 0 0 2px $focus;
			// 	}
			// }

			// &[state="error"] {
			// 	box-shadow: inset 0 0 0 2px $error;
			// }
			// &[state="success"] {
			// 	box-shadow: inset 0 0 0 2px $success;
			// }

			&::placeholder {
				color: $light-color;
			}

			&::-webkit-outer-spin-button,
			&::-webkit-inner-spin-button {
				-webkit-appearance: none;
				margin: 0;
			}

			/* Firefox */
			&[type="number"] {
				-moz-appearance: textfield;
				appearance: textfield;
			}
		}
		> input.flow-input-text:-webkit-autofill,
		> textarea.flow-input-text:-webkit-autofill {
			-webkit-text-fill-color: $text-color !important;
			-webkit-box-shadow: 0 0 0 30px $background inset !important;
		}

		&:focus-within {
			&:not([state="error"]) {
				box-shadow: 0 0 0 1px $focus;
			}
		}
		&[state="error"] {
			box-shadow: 0 0 0 1px $error;
		}

		&[state="success"] {
			.i-state-text {
				color: $success;
			}
		}
		&[state="error"] {
			.i-state-text {
				color: $error;
			}
		}
		&[state="validating"] {
			.i-state-text {
				color: $light-color;
			}
		}
		&[inline] {
			background-color: transparent !important;
			border-bottom: 1px solid $focus;
			border-radius: unset;
			> input.flow-input-text {
				background-color: transparent !important;
				&:hover,
				&:active,
				&:focus {
					-webkit-box-shadow: 0 0 0 30px transparent inset !important;
				}
			}

			&:focus-within {
				&:not([state="error"]) {
					box-shadow: none;
				}

				&[state="error"] {
					box-shadow: none;
				}
			}
		}
	}
}
div.flow-input-wrapper {
	position: relative;
	display: flex;
	align-items: center;
	overflow: hidden;

	> div.flow-input-suffix-tag {
		margin-right: 12px;
		display: inline-flex;
		align-items: center;
	}
	&[boxType="curved"] {
		border-radius: 4px;
	}
	&[boxType="rounded"] {
		border-radius: 18px;
	}

	> div.flow-input-prefix {
		padding: 11px 12px 11px 12px;
		font-size: 14px;
		line-height: 14px;
		position: relative;
		min-height: 36px;
		// &[boxType="curved"] {
		// 	border-radius: 4px 0px 0px 4px;
		// }
		// &[boxType="rounded"] {
		// 	border-radius: 18px 0px 0px 18px;
		// }

		&:after {
			content: "";
			position: absolute;
			top: 8px;
			height: 20px;
			// bottom: 8px;
			right: 0px;
		}

		&[multiline] {
			min-height: 108px;
		}
	}

	// &[prefix] {
	// 	> input.flow-input-text,
	// 	> textarea.flow-input-text {
	// 		// &[boxType="curved"] {
	// 		// 	border-radius: 0px 4px 4px 0px;
	// 		// }
	// 		// &[boxType="rounded"] {
	// 		// 	border-radius: 0px 18px 18px 0px;
	// 		// }
	// 	}
	// }
	&.prefix {
		> input.flow-input-text,
		> textarea.flow-input-text {
			width: auto;
			max-width: 100%;
		}
	}
	> input.flow-input-text,
	> textarea.flow-input-text {
		min-height: 36px;
		border: none;
		flex: auto;
		width: 100%;
		resize: none;
		&[multiline] {
			min-height: 108px;
		}
		&[resize] {
			resize: auto;
		}

		// &[boxType="curved"] {
		// 	border-radius: 4px;
		// }
		// &[boxType="rounded"] {
		// 	border-radius: 18px;
		// }
		padding: 11px 12px 11px 12px;
		font-size: 12px;
		font-weight: normal;
		line-height: 14px;
		box-sizing: border-box;
		font-family: inherit;

		&:focus {
			outline: none;
		}

		&[disabled] {
			cursor: not-allowed;
			opacity: 0.6;
		}

		&::placeholder {
			font-size: 12px;
			font-weight: normal;
			line-height: 14px;
			box-sizing: border-box;
		}
	}

	> div.flow-input-state-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding-left: 12px;
		// padding: 0px 16px 0px 16px;

		> * {
			margin-right: 12px;
		}

		> .i-state-text {
			font-size: 14px;
		}
		// position: absolute;
		// position: absolute;
		// right: 0;
		// top: 10px;
		// padding-right: 12px;

		> .icon-container[name="loading"] > .icon {
			animation: spin 2s linear infinite !important;
		}

		@keyframes spin {
			100% {
				transform: rotate(360deg);
			}
		}
	}

	&[inline] {
		display: inline-flex;
		> input.flow-input-text {
			padding: 0px;
			min-height: 14px;
		}
	}

	&[type="color"] {
		div.flow-input-state-icon {
			display: none;
		}
		> input {
			padding: 8px;
		}
	}
}

@include input-text(
	var(--dashboard-input),
	var(--dashboard-input-focus),
	var(--dashboard-input-placeholder),
	var(--dashboard-font),
	var(--dashboard-danger),
	var(--dashboard-success)
);

.sideout-container {
	@include input-text(
		var(--slideout-input),
		var(--slideout-input-focus),
		var(--slideout-input-placeholder),
		var(--slideout-font),
		var(--slideout-danger),
		var(--slideout-success)
	);
}
</style>
